import { Sparkles, FileText, BookOpenText } from "lucide-react";
import { Button, Text } from "@core/index";
import { useTranslation } from "react-i18next";

export interface TextDocumentToggleProps {
  value: "text" | "document" | string;
  onChange: (value: "text" | "document" | string) => void;
  title?: string;
  theme?: "purple" | "blue" | "green" | "red";
  className?: string;
  newButtonKey?: string;
  newButtonTitle?: string;
  hidePDFInput?: boolean;
}

const themes = {
  purple: {
    titleGradient:
      "from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400",
    activeBorder: "border-purple-500",
    activeBg: "bg-purple-50 dark:bg-purple-900/20",
    hoverBorder: "hover:border-purple-400",
  },
  blue: {
    titleGradient:
      "from-blue-600 to-sky-500 dark:from-blue-400 dark:to-sky-300",
    activeBorder: "border-blue-500",
    activeBg: "bg-blue-50 dark:bg-blue-900/20",
    hoverBorder: "hover:border-blue-400",
  },
  green: {
    titleGradient:
      "from-green-600 to-teal-500 dark:from-green-400 dark:to-teal-300",
    activeBorder: "border-green-500",
    activeBg: "bg-green-50 dark:bg-green-900/20",
    hoverBorder: "hover:border-green-400",
  },
  red: {
    titleGradient:
      "from-red-600 to-orange-500 dark:from-red-400 dark:to-orange-300",
    activeBorder: "border-red-500",
    activeBg: "bg-red-50 dark:bg-red-900/20",
    hoverBorder: "hover:border-red-400",
  },
};

function ModeToggle({
  value,
  onChange,
  title,
  theme = "purple",
  className = "",
  newButtonKey,
  newButtonTitle,
  //just a temporary boolean to hide PDF input
  hidePDFInput = false,
}: TextDocumentToggleProps) {
  const { t } = useTranslation();
  const themeStyles = themes[theme];

  return (
    <div className={`flex flex-col items-center gap-4 mt-2 mb-6 ${className}`}>
      {title && (
        <Text
          variant="h3"
          className={`text-transparent bg-clip-text bg-gradient-to-r ${themeStyles.titleGradient}`}
        >
          {title}
        </Text>
      )}

      <div
        className={`grid grid-cols-2 gap-4 w-full ${newButtonKey && !hidePDFInput ? "grid-cols-3" : ""}`}
      >
        {newButtonKey && (
          <Button
            key={newButtonKey}
            onClick={() => onChange(newButtonKey)}
            type="button"
            rounded="xl"
            bgColor={value === newButtonKey ? themeStyles.activeBg : ""}
            className={`p-4 border-2 ${
              value === newButtonKey
                ? themeStyles.activeBorder
                : `border-gray-200 ${themeStyles.hoverBorder}`
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <BookOpenText className="w-5 h-5" />
              <span>{t(newButtonTitle || "")}</span>
            </div>
          </Button>
        )}
        <Button
          key="text"
          onClick={() => onChange("text")}
          type="button"
          rounded="xl"
          bgColor={value === "text" ? themeStyles.activeBg : ""}
          className={`p-4 border-2 ${
            value === "text"
              ? themeStyles.activeBorder
              : `border-gray-200 ${themeStyles.hoverBorder}`
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Sparkles className="w-5 h-5" />
            <span>{t("podcast.textInput")}</span>
          </div>
        </Button>
        {!hidePDFInput && (
          <Button
            key="document"
            onClick={() => onChange("document")}
            type="button"
            rounded="xl"
            bgColor={value === "document" ? themeStyles.activeBg : ""}
            className={`p-4 border-2 ${
              value === "document"
                ? themeStyles.activeBorder
                : `border-gray-200 ${themeStyles.hoverBorder}`
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>{t("podcast.pdfInput")}</span>
            </div>
          </Button>
        )}
      </div>
    </div>
  );
}

export default ModeToggle;
